// The featured pieces, in one order, for everywhere that shows them.
//
// There are two such places and they had drifted apart: the strip on the home
// page led with the newest, and the rail on Explore put everything added since
// launch after all seventeen compiled-in renders. Featuring a picture therefore
// put it at the front of one and eighteenth on the other, which reads as it not
// being on Explore at all.
//
// Uploaded work leads. That is what the strip's own heading already claims, and
// it is the only order the admin can actually change: a compiled-in render has
// no row and so no position to drag it to. Emptying data/content.ts leaves this
// as a plain sort by position, which is where it is going.

// Ids of the renders that ship with the site, in the order the file lists them.
function builtInOrder(builtIns) {
  return new Map(builtIns.map((piece, index) => [piece.id, index]));
}

export function orderFeatured(pieces, builtIns) {
  const order = builtInOrder(builtIns);

  return pieces
    .filter((piece) => piece.featured)
    .map((piece, seen) => ({
      piece,
      // Uploaded rows come first, in the order they were dragged into. A
      // compiled-in render sorts after all of them, keeping its file order.
      rank: order.has(piece.id)
        ? order.size + order.get(piece.id)
        : Number(piece.position) || 0,
      // Ties keep the order they arrived in rather than being reshuffled on
      // every recompute.
      seen,
    }))
    .sort((a, b) => a.rank - b.rank || a.seen - b.seen)
    .map((entry) => entry.piece);
}

// Waits for pictures to be in the browser's cache before anything swaps to
// them.
//
// The site is prerendered, so a page paints the compiled-in work immediately
// and then the live rows arrive and change what should be on screen. Swapping
// the moment the rows land tears down pictures that were already drawn and puts
// up elements whose files have not been asked for yet: the row empties, the
// layout closes up, and it fills back in one picture at a time. A placeholder
// over the gap does not fix that, it just decorates it.
//
// So the swap waits. The old pictures stay up, doing no harm, until the new
// ones can replace them in a single frame.
export function preloadImages(sources, timeout = 4000) {
  if (typeof window === 'undefined') return Promise.resolve();

  const wanted = [...new Set(sources.filter(Boolean))];
  if (!wanted.length) return Promise.resolve();

  const each = wanted.map(
    (src) =>
      new Promise((resolve) => {
        const probe = new Image();
        // Resolve either way: a picture that 404s must not hold the swap for
        // ever, and the element that replaces it will show its own broken state
        // soon enough.
        probe.onload = resolve;
        probe.onerror = resolve;
        probe.src = src;
      })
  );

  // A slow connection should not mean the new work never appears. Past this the
  // swap happens regardless, which is the old behaviour and no worse than it.
  return Promise.race([
    Promise.all(each),
    new Promise((resolve) => setTimeout(resolve, timeout)),
  ]);
}
